import React, { useEffect } from 'react'

import { UserMenu, useElapsedTime } from '@pb/components'
import { Breakpoints, useMediaQuery } from '@pb/utils'
import { useDecision } from '@optimizely/react-sdk'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import optimizelyConstants from 'constants/optimizely'
import useOptimizelyDecision, {
  useNorthstarABTestEnabled,
} from 'features/account/hooks/useOptimizelyDecision'
import useOptimizelyToggle from 'features/account/hooks/useOptimizelyToggle'
import purchasesSelectors from 'features/account/purchases/duck/selectors'
import settingsSelectors from 'features/account/settings/duck/selectors'
import {
  operationFetchReviewItemsNumber,
  operationSetMenuItemsVisibility,
  operationSetNewMenuDisablement,
  operationSetMenuItemBadges,
} from 'features/account/user-menu/duck/operations'

import userSelector from '../../../duck/selectors'
import { createUserMenuEvents } from '../../../helpers/userMenuHelpers'
import styles from './DynamicUserMenu.scss'

const DynamicUserMenuComponent: React.FC = () => {
  const dispatch = useDispatch()

  const newUserMenus = useSelector(userSelector.getNewUserMenus)
  const shouldFetchReviewItemsNumber = useSelector(userSelector.getShouldFetchReviewItemsNumber)
  const hasActiveOrders = useSelector(purchasesSelectors.getOnlineActiveOrdersStatus)
  const shouldCallRateAndReviewAPI = useSelector(purchasesSelectors.getShouldCallRateAndReviewAPI)
  const shouldShowCompleteProfileReminder = useSelector(
    settingsSelectors.getShowCompleteProfileReminder,
  )
  const hasUserMenuLoaded = useSelector(userSelector.getIsUserMenuLoadedSuccess)
  const isReviewItemRequested = useSelector(userSelector.getIsReviewItemsRequested)

  const isReactPaymentsPageEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_PAYMENTS_REACT,
    internalToggle: 'crmma_payments_react_internal',
  })

  const isNorthstarBrand = useOptimizelyDecision(optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION)

  const isNorthStarABtestEnabled = useNorthstarABTestEnabled(
    optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION,
  )

  const isHelpUsImprovePageEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_HELP_US_IMPROVE,
  })
  const isRateReviewEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_RATE_REVIEW,
  })

  const isShowStaticMenu = useMediaQuery(Breakpoints.XXL)

  const [{ enabled: isAbTestEnabled, variationKey: variation }] = useDecision(
    optimizelyConstants.ACCOUNT_MENU_SUBTEXT_NEWTAG,
    { autoUpdate: true },
  )

  const { getElapsedTime, resetTimer } = useElapsedTime()

  useEffect(() => {
    dispatch(
      operationSetMenuItemsVisibility({
        paymentFeatureFlag: isReactPaymentsPageEnabled,
        shouldHelpUsEnable: isHelpUsImprovePageEnabled,
      }),
    )
  }, [hasUserMenuLoaded])

  useEffect(() => {
    dispatch(operationSetMenuItemBadges(shouldShowCompleteProfileReminder, hasActiveOrders))
  }, [hasActiveOrders])

  useEffect(() => {
    if (shouldFetchReviewItemsNumber && shouldCallRateAndReviewAPI) {
      dispatch(operationFetchReviewItemsNumber(isRateReviewEnabled))
    }
  }, [shouldFetchReviewItemsNumber, shouldCallRateAndReviewAPI])

  useEffect(() => {
    dispatch(operationSetNewMenuDisablement())
  }, [isReviewItemRequested])

  const updatedUserMenu = createUserMenuEvents(
    newUserMenus,
    getElapsedTime,
    resetTimer,
    isNorthStarABtestEnabled,
  )
  const currentRoute = useLocation().pathname
  const matchedMenuItem = updatedUserMenu.find((menuItem) => currentRoute.includes(menuItem.to))
  return (
    <UserMenu
      shouldShowStaticMenu={isShowStaticMenu}
      shouldShowHeading={false}
      menuItems={updatedUserMenu}
      matchedMenuItem={matchedMenuItem}
      shouldShowDynamicUserMenu
      className={styles.userMenuWrapper}
      isAbTestEnabled={isAbTestEnabled}
      variation={variation}
      hasDefaultMenuState={!isNorthstarBrand}
    />
  )
}

export default DynamicUserMenuComponent
